import _ from "lodash";

const isUUID4 = (str) => {
  const uuid4Regex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuid4Regex.test(str);
};

const camelizeKeys = (obj, deep = true) => {
  if (_.isArray(obj)) {
    return _.map(obj, (v) => camelizeKeys(v, deep));
  } else if (_.isPlainObject(obj)) {
    return _.mapKeys(
      deep ? _.mapValues(obj, (value) => camelizeKeys(value, deep)) : obj,
      (value, key) => (isUUID4(key) ? key : _.camelCase(key))
    );
  }
  return obj;
};

const snakeCaseKeys = (obj, deep = true) => {
  if (_.isArray(obj)) {
    return _.map(obj, (v) => snakeCaseKeys(v, deep));
  } else if (_.isPlainObject(obj)) {
    return _.mapKeys(
      deep ? _.mapValues(obj, (value) => snakeCaseKeys(value, deep)) : obj,
      (value, key) => (isUUID4(key) ? key : _.snakeCase(key))
    );
  }
  return obj;
};

const filtersToGet = (filters) => {
  if (!filters) return "";
  return "?" + new URLSearchParams(filters).toString();
};
/**
 * A client for interacting with RESTful API endpoints
 */
export default class RestClient {
  /**
   * Creates a new RestClient instance
   *
   * @param {string} path - The base API path for this resource
   * @param {boolean} [keepCase=false] - Whether to preserve the case of response data keys
   */
  constructor(client, path, keepCase = false) {
    // Normalize path to ensure it doesn't start or end with slashes
    this.path = path.replace(/^\/+|\/+$/g, "");
    this.keepCase = keepCase;
    this.client = client;
  }

  /**
   * Converts response data to camelCase if keepCase is false
   *
   * @param {Object} data - The data to potentially convert
   * @returns {Object} The original or camelized data
   * @private
   */
  toCamel(data) {
    if (this.keepCase) return data;
    data = camelizeKeys(data);
    return this.handleSpecialCamelCase(data);
  }

  handleSpecialSnakeCase(data) {
    return data;
  }
  handleSpecialCamelCase(data) {
    return data;
  }

  toSnake(data) {
    if (this.keepCase) return data;
    data = snakeCaseKeys(data);
    if (_.isArray(data)) {
      return data.map((d) => this.handleSpecialSnakeCase(d));
    }
    return this.handleSpecialSnakeCase(data);
  }

  /**
   * Builds a URL by joining the base path with an endpoint
   *
   * @param {string} [endpoint=''] - The endpoint to append to the base path
   * @param {Object} [queryParams] - Optional query parameters to include
   * @returns {string} The complete URL
   * @private
   */
  buildUrl(endpoint = "", queryParams = null) {
    // Normalize endpoint to remove leading/trailing slashes
    endpoint = endpoint.replace(/^\/+|\/+$/g, "");

    // Combine path and endpoint
    let url = this.path;
    if (endpoint) {
      url = `${url}/${endpoint}`;
    }

    // Ensure trailing slash for RESTful conventions
    if (!url.endsWith("/")) {
      url = `${url}/`;
    }

    // Add query parameters if provided
    if (queryParams) {
      const queryString = filtersToGet(queryParams);
      if (queryString) {
        url = `${url}${queryString.startsWith("?") ? "" : "?"}${queryString}`;
      }
    }

    return url;
  }
  /**
   * Retrieves a list of resources, optionally filtered
   *
   * @param {Object} [filters] - Filter criteria to apply to the list request
   * @param {Object} [config] - Axios request options (e.g. timeout)
   * @returns {Promise<Object>} The response data in camelCase (if configured)
   */
  async listAll(filters, config) {
    filters.page_size = 500;
    const processedFilters = filters ? this.toSnake(filters) : null;
    let url = this.buildUrl("", processedFilters);

    let allResults = [];
    let responseData = null;
    while (url) {
      const response = await this.client.get(url, config);
      if (!responseData) responseData = this.toCamel(response.data);
      const results = response.data.results.map((item) => this.toCamel(item));
      allResults.push(...results);
      url = response.data.next;
    }
    return {
      ...responseData,
      results: allResults,
    };
  }
  /**
   * Retrieves a list of resources, optionally filtered
   *
   * @param {Object} [filters] - Filter criteria to apply to the list request
   * @param {Object} [config] - Axios request options (e.g. timeout)
   * @returns {Promise<Object>} The response data in camelCase (if configured)
   */
  async list(filters, config) {
    const processedFilters = filters ? this.toSnake(filters) : null;
    const url = this.buildUrl("", processedFilters);
    const response = await this.client.get(url, config);
    if (!response.data.results) {
      return this.toCamel(response.data);
    }
    const results = response.data.results.map((item) => this.toCamel(item));
    return {
      ...this.toCamel(response.data),
      results,
    };
  }

  /**
   * Creates a new resource
   *
   * @param {Object} form - The resource data to create
   * @param {Object} [config] - Axios request options (e.g. timeout)
   * @returns {Promise<Object>} The created resource data
   */
  async create(form, config) {
    const url = this.buildUrl();
    form = this.toSnake({ ...form });
    const response = await this.client.post(url, form, config);
    return this.toCamel(response.data);
  }

  /**
   * Updates an existing resource
   *
   * @param {Object} form - The resource data to update
   * @param {string} [uuid] - The unique identifier of the resource
   * @param {Object} [config] - Axios request options (e.g. timeout)
   * @returns {Promise<Object>} The updated resource data
   */
  async update(form, uuid, config) {
    // Use form.uuid as fallback if uuid parameter is not provided
    const resourceId = uuid || form.uuid;

    if (!resourceId) {
      throw new Error("UUID is required for update operation");
    }

    const url = this.buildUrl(resourceId);
    form = this.toSnake({ ...form });
    const response = await this.client.put(url, form, config);
    return this.toCamel(response.data);
  }

  /**
   * Partially updates a resource
   *
   * @param {Object} form - The partial resource data to update
   * @param {string} [uuid] - The unique identifier of the resource
   * @param {Object} [config] - Axios request options (e.g. timeout)
   * @returns {Promise<Object>} The updated resource data
   */
  async partialUpdate(form, uuid, config) {
    // Use form.uuid as fallback if uuid parameter is not provided
    const resourceId = uuid || form.uuid;

    if (!resourceId) {
      throw new Error("UUID is required for partial update operation");
    }

    const url = this.buildUrl(resourceId);
    form = this.toSnake({ ...form });

    const response = await this.client.patch(url, form, config);
    return this.toCamel(response.data);
  }

  /**
   * Deletes a resource
   *
   * @param {string} uuid - The unique identifier of the resource to delete
   * @param {Object} [config] - Axios request options (e.g. timeout)
   * @returns {Promise<Object>} The response data
   */
  async destroy(uuid, config) {
    if (!uuid) {
      throw new Error("UUID is required for delete operation");
    }

    const url = this.buildUrl(uuid);
    const response = await this.client.delete(url, config);
    return this.toCamel(response.data);
  }

  /**
   * Retrieves a specific resource by its identifier
   *
   * @param {string} uuid - The unique identifier of the resource to retrieve
   * @param {Object} [config] - Axios request options (e.g. timeout)
   * @returns {Promise<Object>} The retrieved resource data
   */
  async retrieve(uuid, config) {
    if (!uuid) {
      throw new Error("UUID is required for retrieve operation");
    }

    const url = this.buildUrl(uuid);
    const response = await this.client.get(url, config);
    return this.toCamel(response.data);
  }

  /**
   * Creates multiple resources in a single request
   *
   * @param {Object} form - The bulk resource data to create
   * @param {Object} [config] - Axios request options (e.g. timeout)
   * @returns {Promise<Object>} The created resources data
   */
  async bulkCreate(form, config) {
    const url = this.buildUrl("bulk_create");
    const response = await this.client.post(
      url,
      this.toSnake(form, { deep: true }),
      config
    );
    return this.toCamel(response.data);
  }

  /**
   * Destroy multiple resources in a single request
   * @param {Object} form - The bulk resource data to delete
   * @param {Object} [config] - Axios request options (e.g. timeout)
   * @return {Promise<Object>} The response data
   */
  async bulkDestroy(form, config) {
    const url = this.buildUrl("bulk_destroy");
    const response = await this.client.post(
      url,
      this.toSnake(form, { deep: true }),
      config
    );
    return this.toCamel(response.data);
  }

  /**
   * Updates multiple resources in a single request
   *
   * @param {Object} form - The bulk resource data to create
   * @param {Object} [config] - Axios request options (e.g. timeout)
   * @returns {Promise<Object>} The created resources data
   */
  async bulkUpdate(form, config) {
    const url = this.buildUrl("bulk_update");
    const response = await this.client.put(
      url,
      this.toSnake(form, { deep: true }),
      config
    );
    return this.toCamel(response.data);
  }
}
